document.addEventListener("alpine:init", () => {
    Alpine.data("productOptions", () => ({
        bending: false,
        showVariantsWithInput: window.productAttributes?.showVariantsWithInput,
        surchargeProducts: {
            length: { variants: [], productHandle: null, label: null },
            width: { variants: [], productHandle: null, label: null }
        },
        productVariants: null,
        productOptionNames: null,
        variantInputs: {},
        addingToCart: false,
        optionsAbortController: null,
        errors: {
            length: false,
            width: false,
            sqm: false,
            incomplete: false
        },
        missingFields: [],
        mainProduct: {
            variantId: null,
            quantity: 1,
            price: null,
            properties: {
                length: null,
                width: null,
                _calculationType: window.productAttributes?.calculationType,
                _multiplier: 1,
                _totalBasePrice: null, // mainProduct + upsellProducts * 1
                _id: null,
            }
        },
        upsellProducts: [],

        init() {
            this.mainProduct.properties._id = this.generateUniqueId();
            this.initVariants()
            this.initSurcharges()

            this.$watch('mainProduct.quantity', (value) => {
                if (value < 1 || isNaN(value)) {
                    this.mainProduct.quantity = 1;
                    return;
                }

                this.upsellProducts.forEach(product => {
                    product.quantity = product.properties._baseQuantity * value;
                });
            });

            this.$watch('mainProduct.properties', () => this.validateInputs())

            if (this.showVariantsWithInput) {
                this.$watch('mainProduct.properties.length', () => this.findVariantByInputs());
                this.$watch('mainProduct.properties.width', () => this.findVariantByInputs());
            }

            this.$watch('mainProduct.properties.length', () => this.updateSurchargeProducts());
            this.$watch('mainProduct.properties.width', () => this.updateSurchargeProducts());

            /* Radios and selects do not touch mainProduct.properties, so the deep watcher
               above never sees them. Delegated on the component root, which outlives every
               section update, so it does not need re-binding. */
            ['change', 'input'].forEach(event =>
                this.$el.addEventListener(event, () => this.validateInputs())
            );

            // Children fill their x-model values first, so evaluate once they are up
            this.$nextTick(() => this.validateInputs());
            document.addEventListener('liquiflow:set-main-product', () => {
                this.validateInputs()
            })
        },

        generateUniqueId() {
            return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
        },

        cleanProperties(properties) {
            return Object.fromEntries(
                Object.entries(properties).filter(([, value]) => value !== null && value !== undefined && value !== '')
            );
        },

        /* Returns a finite number or null. Empty inputs and unset metafields must
           not fall through as 0 - parseFloat(null) and isNaN(null) would do that. */
        readNumber(value) {
            if (value === null || value === undefined || value === '') return null;
            const number = parseFloat(value);
            return Number.isFinite(number) ? number : null;
        },

        /* Option values may carry a unit or a decimal comma ("100 cm", "56,5"), so
           normalise before parsing. parseInt would silently truncate the decimals.
           Returns NaN for non-numeric values like the "x" placeholder. */
        readOptionNumber(value) {
            if (value === null || value === undefined) return NaN;
            return parseFloat(String(value).replace(',', '.'));
        },

        /* Shopify shows every line item property whose key does not start with an
           underscore - in the checkout and in the order, and neither of the two is
           themeable. So the measurements the customer should read get a proper name
           and a unit, while the raw numbers travel as private twins for the price
           recalculation in the backend. Override the names from Liquid by putting a
           labels object on window.productAttributes. */
        propertyLabels: {
            length: 'Länge',
            width: 'Breite',
            color: 'Farbwunsch',
            ...(window.productAttributes?.labels ?? {})
        },

        // These strings are read, not calculated with, so use the German separator
        formatMeasure(value) {
            return String(value).replace('.', ',');
        },

        displayProperties() {
            const { length: lengthLabel, width: widthLabel, color: colorLabel } = this.propertyLabels;
            const properties = {};

            const length = this.readNumber(this.mainProduct.properties.length);
            const width = this.readNumber(this.mainProduct.properties.width);
            const color = this.mainProduct.properties.color;

            if (length !== null) properties[lengthLabel] = `${this.formatMeasure(length)} cm`;
            if (width !== null) properties[widthLabel] = `${this.formatMeasure(width)} cm`;
            if (color) properties[colorLabel] = color;

            return properties;
        },

        debounce(fn, wait = 250) {
            let timeout;
            return (...args) => {
                clearTimeout(timeout);
                timeout = setTimeout(() => fn.apply(this, args), wait);
            };
        },

        roundPrice(price) {
            const roundTo = this.readNumber(window.productAttributes?.roundTo);
            if (roundTo === null || roundTo <= 0) return price;
            return Math.round(price / roundTo) * roundTo;
        },

        /* Price of one configured unit in cents: the main product including its
           dimension factor, plus every upsell, times the suspension multiplier.
           Kept free of side effects so it is safe to call from a getter. */
        computeBasePrice() {
            const properties = this.mainProduct.properties;
            const price = this.readNumber(this.mainProduct.price);
            if (price === null) return null;

            const length = this.readNumber(properties.length);
            const width = this.readNumber(properties.width);

            let basePrice = price;

            switch (properties._calculationType) {
                // Products that only have length, price per meter
                case "length":
                    if (length === null) return null;
                    basePrice = this.roundPrice(basePrice * length / 100);
                    break;
                // Products that only have width, price per meter
                case "width":
                    if (width === null) return null;
                    basePrice = this.roundPrice(basePrice * width / 100);
                    break;
                // Products that have both, price per qm
                case "both":
                    if (length === null || width === null) return null;
                    basePrice = this.roundPrice(basePrice * (length * width) / 10000);
                    break;
            }

            const upsellSum = this.upsellProducts.reduce((sum, p) => {
                const unitPrice = this.readNumber(p.variantPrice) ?? 0;
                const baseQuantity = this.readNumber(p.properties._baseQuantity) ?? 1;
                let itemPrice = unitPrice * baseQuantity;

                // If it's a suspension product and it's based on width,
                // we multiply it with the width. Without a width there is no
                // factor to apply, so the unscaled price stands.
                if (p.properties._basedOnWidth && width !== null) {
                    itemPrice = this.roundPrice(itemPrice * width / 100);
                }

                if (p.properties._basedOnLength && length !== null) {
                    itemPrice = this.roundPrice(itemPrice * length / 100);
                }

                return sum + itemPrice;
            }, 0);

            const multiplier = this.readNumber(properties._multiplier);
            const factor = multiplier !== null && multiplier > 0 ? multiplier : 1;

            // Cents are the smallest unit Shopify accepts, so settle the value here
            // instead of passing fractions on to the cart.
            return Math.round((upsellSum + basePrice) * factor);
        },

        get totalPrice() {
            const basePrice = this.computeBasePrice();
            if (basePrice === null) return 0;
            return basePrice * this.mainProduct.quantity;
        },

        increaseQuantity() {
            this.mainProduct.quantity++;
        },

        decreaseQuantity() {
            this.mainProduct.quantity = Math.max(1, (this.readNumber(this.mainProduct.quantity) ?? 1) - 1);
        },

        /* A dimension is validated as soon as one of its bounds is configured.
           An empty input then counts as an error - the field is required. */
        isOutOfRange(value, min, max) {
            if (min === null && max === null) return false
            if (value === null) return true
            if (min !== null && value < min) return true
            if (max !== null && value > max) return true
            return false
        },

        validateInputs() {
            const attributes = window.productAttributes
            if (!attributes) return

            const lengthMin = this.readNumber(attributes.lengthMin)
            const lengthMax = this.readNumber(attributes.lengthMax)
            const widthMin = this.readNumber(attributes.widthMin)
            const widthMax = this.readNumber(attributes.widthMax)
            const sqmMin = this.readNumber(attributes.sqmMin)
            const sqmMax = this.readNumber(attributes.sqmMax)

            const length = this.readNumber(this.mainProduct.properties.length)
            const width = this.readNumber(this.mainProduct.properties.width)

            this.errors.length = this.isOutOfRange(length, lengthMin, lengthMax)
            this.errors.width = this.isOutOfRange(width, widthMin, widthMax)

            if ((sqmMin !== null || sqmMax !== null) && length !== null && width !== null) {
                const sqm = (length * width) / 10000
                this.errors.sqm = (sqmMin !== null && sqm < sqmMin) || (sqmMax !== null && sqm > sqmMax)
            } else {
                this.errors.sqm = false
            }

            this.missingFields = this.checkCompleteness()
            this.errors.incomplete = this.missingFields.length > 0
        },

        /* Name a field for the warning. Radios are named by their fieldset, because
           their own label is the option value rather than what is being chosen. */
        fieldLabel(el) {
            const name = el.type === 'radio'
                ? el.closest('fieldset')?.querySelector('legend')?.textContent
                : el.closest('label')?.querySelector('span')?.textContent

            return (name || el.name || '').trim().replace(/:$/, '')
        },

        /* Every field the configurator renders has to carry a value before the cart
           accepts the configuration. A radio group counts as filled once one of its
           options is checked, which is what catches suspension and holder - the server
           renders those without a preselection. Checkboxes are optional by nature (the
           bending toggle is one), and a field marked data-optional is skipped. */
        checkCompleteness() {
            const form = this.$el.querySelector('[data-accordion="wrap"]')
            if (!form) return []

            const missing = []
            const seenGroups = new Set()

            form.querySelectorAll('input, select, textarea').forEach(el => {
                if (el.disabled || el.type === 'checkbox' || el.type === 'hidden') return
                if (el.hasAttribute('data-optional')) return

                if (el.type === 'radio') {
                    if (!el.name || seenGroups.has(el.name)) return
                    seenGroups.add(el.name)

                    const group = form.querySelectorAll(`input[type="radio"][name="${CSS.escape(el.name)}"]`)
                    if (![...group].some(radio => radio.checked)) missing.push(this.fieldLabel(el))
                    return
                }

                if (String(el.value).trim() === '') missing.push(this.fieldLabel(el))
            })

            return missing
        },

        hasErrors() {
            return this.errors.length || this.errors.width || this.errors.sqm || this.errors.incomplete
        },

        /* Single source of truth for the button state, so the template does not have to
           combine several conditions - and nothing fights over the disabled attribute. */
        get canAddToCart() {
            return !this.addingToCart && !!this.mainProduct.variantId && !this.hasErrors()
        },

        get missingFieldsText() {
            return this.missingFields.join(', ')
        },

        selectOption(el) {
            if (!el.checked) return
            const option2Value = el.value

            const wrapper = el.closest('[data-option="wrap"]')
            if (!wrapper) return

            const json = wrapper.querySelector('[data-option="script"]')
            if (!json) return

            let variantData
            try {
                variantData = JSON.parse(json.textContent)
            } catch {
                console.error('Could not parse variant data')
                return
            }

            const calculationType = this.mainProduct.properties._calculationType

            if (calculationType === 'width') {
                const width = this.readNumber(this.mainProduct.properties.width)
                let basedOnWidth = true

                if (width === null) return

                const variant = this.findVariant(width, variantData, option2Value, 'width')
                if (!variant) return

                const option1Number = this.readOptionNumber(variant.option1)
                if (!isNaN(option1Number)) {
                    this.mainProduct.properties.width = option1Number
                    basedOnWidth = false
                }

                // Always pass a multiplier so switching to a variant without one
                // resets it instead of keeping the previous selection's value.
                const variantMultiplier = this.readNumber(variant.multiplier)

                this.addProduct({
                    productHandle: variantData.product_handle,
                    variantId: variant.id,
                    variantPrice: variant.price,
                    multiplier: variantMultiplier !== null && variantMultiplier > 0 ? variantMultiplier : 1,
                    basedOnWidth,
                    ...this.optionLabels(wrapper, variantData, variant)
                })
            } else {
                const length = this.readNumber(this.mainProduct.properties.length)
                if (length === null) return

                const variant = this.findVariant(length, variantData, option2Value, 'length')
                if (!variant) return

                this.addProduct({
                    productHandle: variantData.product_handle,
                    variantId: variant.id,
                    variantPrice: variant.price,
                    baseQuantity: variant.quantity,
                    ...this.optionLabels(wrapper, variantData, variant)
                })
            }
        },

        /* How suspension and holder are named on the collapsed cart line. Both parts can
           be overridden, and each falls back one step at a time:

             label  <-  "label" in the option JSON
                    <-  data-option-label on the [data-option="wrap"] element
                    <-  nothing, the mini cart then uses the real product title
             value  <-  "label" on the selected variant in the option JSON
                    <-  its option2 value
                    <-  nothing, the mini cart then uses the real variant title

           Resolved at selection time because only here is it known which variant the
           customer actually ended up with. */
        optionLabels(wrapper, variantData, variant) {
            return {
                productTitle: variantData?.label || wrapper.dataset.optionLabel || null,
                variantTitle: variant?.label || variant?.option2 || null
            }
        },

        findVariant(size, variantData, option2Value, sizeType = 'length') {
            const variants = variantData.variants
            const optionsWithValues = variantData.options

            if (!optionsWithValues || optionsWithValues.length < 1) return null

            const option1Data = optionsWithValues[0]
            if (!option1Data || !option1Data.values) return null

            const numSize = parseFloat(size)
            if (isNaN(numSize)) {
                return null
            }

            const values = option1Data.values.map(v => ({
                name: v,
                numValue: this.readOptionNumber(v)
            }))

            const numericValues = values.filter(v => !isNaN(v.numValue))
            numericValues.sort((a, b) => a.numValue - b.numValue)

            const findVariantByOptions = (option1Value) => {
                return variants.find(variant =>
                    variant.option1 === option1Value && variant.option2 === option2Value
                )
            }

            if (sizeType === 'width') {
                for (let i = 0; i < numericValues.length; i++) {
                    if (numericValues[i].numValue >= numSize) {
                        const result = findVariantByOptions(numericValues[i].name)
                        if (result) return result
                    }
                }

                if (numericValues.length > 0) {
                    const result = findVariantByOptions(numericValues[numericValues.length - 1].name)
                    if (result) return result
                }
            } else {
                for (let i = 0; i < numericValues.length; i++) {
                    const currentValue = numericValues[i].numValue
                    const nextValue = i + 1 < numericValues.length ? numericValues[i + 1].numValue : Infinity

                    if (numSize >= currentValue && numSize < nextValue) {
                        const result = findVariantByOptions(numericValues[i].name)
                        if (result) return result
                    }
                }

                if (numericValues.length > 0) {
                    const lastValue = numericValues[numericValues.length - 1]
                    if (numSize >= lastValue.numValue) {
                        const result = findVariantByOptions(lastValue.name)
                        if (result) return result
                    }
                }
            }

            /* If no numeric variant matches, we select the variant with an "x" as an option value */
            const xValue = values.find(v => v.name.toLowerCase() === 'x')
            if (xValue) {
                return findVariantByOptions(xValue.name)
            }

            return null
        },

        initVariants(handle) {
            if (handle) {
                if (!this.variantInputs[handle]) {
                    this.variantInputs[handle] = { length: null, width: null };
                }
                const debounced = this.debounce(() => this.findVariantByInputs(handle), 500);
                this.$watch(`variantInputs['${handle}'].length`, debounced);
                this.$watch(`variantInputs['${handle}'].width`, debounced);
                return;
            }

            const scriptEl = document.querySelector('[data-product-variants="main"]');
            if (!scriptEl) return;

            let variantData;
            try {
                variantData = JSON.parse(scriptEl.textContent);
            } catch {
                console.error('Could not parse product variants data');
                return;
            }

            const variants = variantData?.variants;
            if (!variants?.length) return;

            this.productVariants = variants;
            this.productOptionNames = Array.isArray(variantData?.options) ? variantData.options : null;
        },

        findVariantByInputs(handle) {
            let variants;

            if (handle) {
                const scriptEl = document.querySelector(`[data-product-variants="upsell"][data-product-handle="${handle}"]`);
                if (!scriptEl) return;
                try {
                    variants = JSON.parse(scriptEl.textContent)?.variants;
                } catch {
                    console.error('Could not parse product variants data');
                    return;
                }
            } else {
                variants = this.productVariants;
            }

            if (!variants?.length) return;

            const inputs = handle ? (this.variantInputs[handle] ?? {}) : this.mainProduct.properties;
            const length = parseFloat(inputs.length);
            const width = parseFloat(inputs.width);

            const uniqueSorted = (getter) =>
                [...new Set(variants.map(getter))]
                    .map(v => ({ raw: v, num: this.readOptionNumber(v) }))
                    .filter(v => !isNaN(v.num))
                    .sort((a, b) => a.num - b.num);

            /* Which variant option carries which measure: option1 = length and
               option2 = width by position, unless the main-product JSON names its
               options — then "Breite"/"Width" binds to the width input and
               "Länge"/"Höhe"/"Length"/"Height" to the length input, wherever they
               sit. Single-axis products (e.g. width tiers as the only option)
               only resolve correctly by name. */
            const getters = [v => v.option1, v => v.option2, v => v.option3];
            let lengthIndex = 0;
            let widthIndex = 1;
            const optionNames = handle ? null : this.productOptionNames;
            if (optionNames?.length) {
                const widthByName = optionNames.findIndex(n => /breit|width/i.test(String(n)));
                const lengthByName = optionNames.findIndex(n => /l[aä]ng|h[oö]h|length|height/i.test(String(n)));
                if (widthByName !== -1 || lengthByName !== -1) {
                    widthIndex = widthByName;
                    lengthIndex = lengthByName;
                }
            }

            const lengthValues = lengthIndex !== -1 ? uniqueSorted(getters[lengthIndex]) : [];
            const widthValues = widthIndex !== -1 ? uniqueSorted(getters[widthIndex]) : [];

            if (lengthValues.length && isNaN(length)) return;
            if (widthValues.length && isNaN(width)) return;

            // Clamp inputs to max available option values
            if (lengthValues.length && !isNaN(length)) {
                const maxLength = lengthValues[lengthValues.length - 1].num;
                if (length > maxLength) {
                    if (handle) {
                        this.variantInputs[handle].length = maxLength;
                    } else {
                        this.mainProduct.properties.length = maxLength;
                    }
                    return;
                }
            }

            if (widthValues.length && !isNaN(width)) {
                const maxWidth = widthValues[widthValues.length - 1].num;
                if (width > maxWidth) {
                    if (handle) {
                        this.variantInputs[handle].width = maxWidth;
                    } else {
                        this.mainProduct.properties.width = maxWidth;
                    }
                    return;
                }
            }

            const nextLength = lengthValues.length
                ? (lengthValues.find(v => v.num >= length) ?? lengthValues[lengthValues.length - 1])
                : null;
            const nextWidth = widthValues.length
                ? (widthValues.find(v => v.num >= width) ?? widthValues[widthValues.length - 1])
                : null;

            if (!nextLength && !nextWidth) return;

            const matched = variants.find(v =>
                (!nextLength || getters[lengthIndex](v) === nextLength.raw) &&
                (!nextWidth || getters[widthIndex](v) === nextWidth.raw)
            );
            if (!matched) return;

            if (handle) {
                if (nextLength) this.variantInputs[handle].length = nextLength.num;
                if (nextWidth) this.variantInputs[handle].width = nextWidth.num;
                this.addProduct({ productHandle: handle, variantId: matched.id, variantPrice: matched.price });
            } else {
                this.fetchOptions(null, matched.id);
            }
        },

        initSurcharges() {
            ['length', 'width'].forEach(type => {
                const scriptEl = document.querySelector(`[data-product-surcharge="${type}"]`);
                if (!scriptEl) return;

                let surchargeData;
                try {
                    surchargeData = JSON.parse(scriptEl.textContent);
                } catch {
                    console.error(`Could not parse surcharge data for ${type}`);
                    return;
                }

                const variants = surchargeData?.variants;
                const productHandle = surchargeData?.product_handle;
                if (!variants?.length || !productHandle) return;

                // Optional "label" renames the surcharge on the collapsed cart line
                const label = surchargeData?.label || null;

                switch (type) {
                    case "length":
                        this.surchargeProducts.length.variants = variants
                        this.surchargeProducts.length.productHandle = productHandle
                        this.surchargeProducts.length.label = label
                        break;
                    case "width":
                        this.surchargeProducts.width.variants = variants
                        this.surchargeProducts.width.productHandle = productHandle
                        this.surchargeProducts.width.label = label
                        break;
                }
            })
        },

        updateSurchargeProducts() {
            ['length', 'width'].forEach(type => {
                const variants = this.surchargeProducts[type].variants;
                const productHandle = this.surchargeProducts[type].productHandle;
                if (!variants?.length || !productHandle) return;

                const currentValue = this.readNumber(
                    type === 'length' ? this.mainProduct.properties.length : this.mainProduct.properties.width
                );
                if (currentValue === null) return;

                // Find the highest reached threshold (option1 > 0 and currentValue >= option1)
                const matched = variants
                    .map(v => ({ ...v, threshold: this.readOptionNumber(v.option1) }))
                    .filter(v => !isNaN(v.threshold) && v.threshold > 0 && currentValue >= v.threshold)
                    .sort((a, b) => b.threshold - a.threshold)[0];

                if (!matched) {
                    this.upsellProducts = this.upsellProducts.filter(p => p.productHandle !== productHandle);
                    return;
                }

                this.addProduct({
                    productHandle,
                    variantId: matched.id,
                    variantPrice: matched.price,
                    productTitle: this.surchargeProducts[type].label,
                    variantTitle: matched.label || null,
                    isSurcharge: true
                });
            });
        },

        addProduct({
            productHandle,
            variantId,
            variantPrice,
            baseQuantity = 1,
            multiplier = null,
            basedOnWidth = false,
            basedOnLength = false,
            variantTitle = null,
            productTitle = null,
            isSurcharge = false
        }) {
            const existingIndex = this.upsellProducts.findIndex(p => p.productHandle === productHandle);
            const existing = existingIndex > -1 ? this.upsellProducts[existingIndex] : null;

            // A base quantity of 0 (missing holder_count metafield) would price the
            // upsell as 1 but order 0 of it, so settle on at least 1 for both.
            const safeBaseQuantity = this.readNumber(baseQuantity) > 0 ? this.readNumber(baseQuantity) : 1;
            const quantity = safeBaseQuantity * this.mainProduct.quantity;

            /* Callers that only refresh the variant must not wipe the display names. */
            const label = productTitle ?? existing?.productTitle ?? null;
            const value = variantTitle ?? existing?.variantTitle ?? null;

            if (multiplier !== null) {
                this.mainProduct.properties._multiplier = multiplier
            }

            /* _displayLabel and _displayValue travel with the cart line, so the mini cart
               can name the component on the collapsed line without having to fall back to
               the real Shopify product title. Underscored, because the component lines
               themselves are never shown to the customer. */
            const properties = {
                _mainProductId: this.mainProduct.properties._id,
                _baseQuantity: safeBaseQuantity,
                _basedOnWidth: basedOnWidth,
                _basedOnLength: basedOnLength,
                _displayLabel: label,
                _displayValue: value,
                _isSurcharge: isSurcharge
            };

            if (existingIndex > -1) {
                this.upsellProducts = this.upsellProducts.map((p, i) =>
                    i === existingIndex
                        ? {
                            ...p,
                            variantId,
                            variantPrice,
                            quantity,
                            variantTitle: value,
                            productTitle: label,
                            properties: { ...p.properties, ...properties }
                        }
                        : p
                );
            } else {
                this.upsellProducts.push({
                    productHandle,
                    variantId,
                    variantPrice,
                    quantity,
                    variantTitle: value,
                    productTitle: label,
                    properties
                });
            }
        },

        async addAllToCart() {
            if (this.addingToCart) return;

            this.validateInputs();
            if (this.hasErrors() || !this.mainProduct.variantId) return;

            /* The mini cart extrapolates every line from _totalBasePrice, so it is
               computed here explicitly. Deriving it from the price getter would tie
               the cart to the price having been rendered at least once. */
            const basePrice = this.computeBasePrice();
            if (basePrice === null) return;
            this.mainProduct.properties._totalBasePrice = basePrice;

            this.addingToCart = true;

            const url = `${window.Shopify.routes.root}cart/add.js`;
            const uniqueId = this.generateUniqueId();

            const items = this.upsellProducts.map(p => ({
                id: p.variantId,
                quantity: p.quantity,
                properties: this.cleanProperties({
                    ...p.properties,
                    _mainProductId: uniqueId
                })
            }));

            /* The upsell titles used to be copied onto the main line here. The mini cart
               now derives them at checkout time from the actual cart lines, where the
               real product and variant names are available, so this only duplicated the
               information - and only for the callers that happened to pass titles at
               all, which left suspension, holder and surcharges out. */

            /* The measurements change key on their way into the cart: the customer reads
               the named ones, the backend calculates with the private twins. Display
               properties come first because the checkout renders in insertion order. */
            const { length, width, color, ...technical } = this.mainProduct.properties;

            items.push({
                id: this.mainProduct.variantId,
                quantity: this.mainProduct.quantity,
                properties: this.cleanProperties({
                    ...this.displayProperties(),
                    ...technical,
                    _length: length,
                    _width: width,
                    _id: uniqueId
                })
            })

            try {
                const response = await fetch(url, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ items })
                });

                if (!response.ok) throw new Error('Cart response not ok');

                const result = await response.json();

                this.mainProduct.properties._id = this.generateUniqueId();

                ['cartupdated', 'toggleminicart'].forEach(name =>
                    document.dispatchEvent(new Event(name, { bubbles: true }))
                );

                return result;
            } catch (error) {
                console.error("Cart error:", error);
            } finally {
                this.addingToCart = false;
            }
        },

        /* Morphing keeps the surviving DOM nodes, and with them the customer's
           selection and the focused field. It needs the variant data attributes on
           the rendered content: x-init does not re-run on a morphed element, so the
           price would go stale. Without the plugin or the attributes we fall back to
           replacing the markup, which is the behaviour we had before. */
        canMorphSection(source) {
            return typeof window.Alpine?.morph === 'function' && !!source;
        },

        /* Upsell options are never rendered as selected by the server, so their state
           has to be carried across the patch. Main product options carry
           data-option-value-id and are left to the server, which may legitimately
           correct the selection to an available variant. */
        captureSelection(wrapper) {
            const selected = new Set();
            wrapper.querySelectorAll('input[type="radio"], input[type="checkbox"]').forEach(el => {
                if (!el.checked || !el.name || el.hasAttribute('data-option-value-id')) return;
                selected.add(JSON.stringify([el.name, el.value]));
            });
            return selected;
        },

        restoreSelection(wrapper, selected) {
            if (!selected.size) return;
            wrapper.querySelectorAll('input[type="radio"], input[type="checkbox"]').forEach(el => {
                if (!el.name || el.hasAttribute('data-option-value-id')) return;
                if (selected.has(JSON.stringify([el.name, el.value]))) el.checked = true;
            });
        },

        captureFocus(wrapper) {
            const el = document.activeElement;
            if (!el || !el.name || !wrapper.contains(el)) return null;

            const focus = { name: el.name, value: el.value };

            // Number inputs report null here and reject setSelectionRange
            if (typeof el.selectionStart === 'number') {
                focus.selectionStart = el.selectionStart;
                focus.selectionEnd = el.selectionEnd;
            }

            return focus;
        },

        restoreFocus(wrapper, focus) {
            // Morph usually keeps the node, and with it the focus
            if (!focus || wrapper.contains(document.activeElement)) return;

            const candidates = Array.from(wrapper.querySelectorAll(`[name="${CSS.escape(focus.name)}"]`));
            const el = candidates.find(c => c.value === focus.value) ?? candidates[0];
            if (!el) return;

            el.focus({ preventScroll: true });
            if (focus.selectionStart === undefined) return;

            try {
                el.setSelectionRange(focus.selectionStart, focus.selectionEnd);
            } catch {
                /* input type does not support a selection range */
            }
        },

        /* [data-replace] regions nest - price and add-to-cart sit inside
           product-content - so only the outermost ones may be updated. Patching a
           region that the surrounding patch already rebuilt corrupts Alpine's
           bookkeeping over the nodes it initialised. */
        sectionRegions(html) {
            return Array.from(html.querySelectorAll('[data-replace]'))
                .filter(el => !el.parentElement?.closest('[data-replace]'));
        },

        /* x-ref resolves its x-data root while it initialises, which morph cannot
           guarantee for nodes it rebuilds - the root comes back undefined and Alpine
           throws on root._x_refs. A throw inside morph is not survivable: it happens
           inside Alpine.mutateDom, which stops the mutation observer before the
           callback and only restarts it afterwards, so every later DOM update would
           go uninitialised. Nothing in the theme reads $refs.form, so the attribute
           is dropped from the incoming markup instead. */
        stripRefs(el) {
            el.querySelectorAll('[x-ref]').forEach(refEl => refEl.removeAttribute('x-ref'));
        },

        /* Some attributes belong to client side scripts, not to the server render, and
           morph would strip them because the fresh markup does not carry them. Copying
           them onto the incoming node keeps the patch from touching them.

           data-script-initialized is the important one: the accordion, slider and tab
           scripts use it as their "already wired" guard. Losing it makes their init run
           a second time over DOM that survived the patch, which binds every toggle
           twice. is-active and the inline height GSAP writes are what keeps an open
           accordion open. */
        preserveClientState(el, toEl) {
            if (el.nodeType !== 1 || toEl?.nodeType !== 1) return;

            if (el.dataset.scriptInitialized) {
                toEl.dataset.scriptInitialized = el.dataset.scriptInitialized;
            }

            if (el.matches("[data-accordion='component']") && el.classList.contains('is-active')) {
                toEl.classList.add('is-active');
            }

            // Scoped on purpose - elsewhere the inline style comes from Liquid
            if (el.matches("[data-accordion='content']") && el.hasAttribute('style')) {
                toEl.setAttribute('style', el.getAttribute('style'));
            }

            if (el.matches("[data-accordion='toggle']") && toEl.hasAttribute('aria-expanded')) {
                toEl.setAttribute('aria-expanded', el.getAttribute('aria-expanded') ?? 'false');
            }
        },

        applyConfigOption(el) {
            const d = el.dataset;
            const variantId = Number(d.variantId);
            if (!variantId) return;
            this.addProduct({
                productHandle: d.productHandle,
                variantId,
                variantPrice: Number(d.variantPrice),
                variantTitle: d.variantTitle || null,
                productTitle: d.productTitle || null,
                basedOnWidth: d.basedOnWidth === "true",
                basedOnLength: d.basedOnLength === "true"
            });
        },

        /* Returns the variant id the section was rendered for, or null when the
           markup was replaced and x-init has already applied the variant itself. */
        applySection(wrapper, html) {
            const source = html.querySelector('[data-replace="product-content"] [data-variant-id]');
            const morphing = this.canMorphSection(source);

            /* Captured for both paths: replacing the markup loses the selection and the
               focus just as much, it just does not lose the Alpine state around it. */
            const selected = this.captureSelection(wrapper);
            const focus = this.captureFocus(wrapper);

            this.sectionRegions(html).forEach(newEl => {
                const target = wrapper.querySelector(`[data-replace="${newEl.dataset.replace}"]`);
                if (!target) return;

                if (!morphing) {
                    target.innerHTML = newEl.innerHTML;
                    return;
                }

                this.stripRefs(newEl);

                try {
                    window.Alpine.morph(target, newEl.outerHTML, {
                        updating: (el, toEl) => this.preserveClientState(el, toEl)
                    });
                } catch (error) {
                    /* Restart the observer Alpine left stopped, otherwise nothing that
                       follows would ever be initialised again. */
                    window.Alpine.startObservingMutations?.();
                    console.warn('Morph failed, replacing markup instead:', error);
                    target.innerHTML = newEl.innerHTML;
                }
            });

            // On the morph path x-init did not re-run, so apply the variant it would have set
            const variantId = morphing ? source.dataset.variantId : null;
            if (morphing) {
                const variantPrice = this.readNumber(source.dataset.variantPrice);
                if (variantId) this.mainProduct.variantId = Number(variantId);
                if (variantPrice !== null) this.mainProduct.price = variantPrice;
            }

            this.restoreSelection(wrapper, selected);
            this.restoreFocus(wrapper, focus);

            this.mainProduct.properties._multiplier = 1;
            wrapper.querySelectorAll('[data-option="wrap"] input:checked').forEach(el => this.selectOption(el));
            wrapper.querySelectorAll("input[data-config-option]:checked").forEach(el => this.applyConfigOption(el))
            this.validateInputs();

            return variantId;
        },

        async fetchOptions(el, variantId = null) {
            document.dispatchEvent(new Event('liquiflow:before-set-main-product', { bubbles: true }))

            const wrapper = el
                ? el.closest('[data-section="wrap"]')
                : document.querySelector('[data-section="wrap"]');
            if (!wrapper?.dataset.sectionId) return;

            const url = new URL(window.location.pathname, window.location.origin);
            url.searchParams.set('section_id', wrapper.dataset.sectionId);

            if (variantId) {
                url.searchParams.set('variant', variantId);
            } else {
                const checkedSelectors = wrapper.querySelectorAll('[data-option-value-id]:checked');
                const selectedValues = Array.from(checkedSelectors).map(s => s.dataset.optionValueId);

                const selectElements = wrapper.querySelectorAll('select[data-option-select]');
                selectElements.forEach(select => {
                    if (select.value) {
                        selectedValues.push(select.value);
                    }
                });

                if (selectedValues.length) url.searchParams.set('option_values', selectedValues.join(','));
            }

            // A newer selection wins, so drop any request still in flight
            this.optionsAbortController?.abort();
            this.optionsAbortController = new AbortController();

            try {
                const response = await fetch(url.toString(), { signal: this.optionsAbortController.signal });
                if (!response.ok) throw new Error('Section response not ok');

                const htmlText = await response.text();
                const html = new DOMParser().parseFromString(htmlText, 'text/html');

                /* The element we started from may have been replaced while the request
                   was in flight. Patching into a detached tree leaves Alpine without a
                   reachable x-data root, so resolve the live section again. */
                const liveWrapper = document.querySelector(`[data-section="wrap"][data-section-id="${wrapper.dataset.sectionId}"]`);
                if (!liveWrapper) return;

                const newVariantId = this.applySection(liveWrapper, html);

                if (newVariantId) {
                    const currentUrl = new URL(window.location.href);
                    currentUrl.searchParams.set('variant', newVariantId);
                    window.history.replaceState({}, '', currentUrl.toString());
                }

                document.dispatchEvent(new Event('liquiflow:set-main-product', { bubbles: true }))
            } catch (error) {
                if (error.name === 'AbortError') return;
                console.error("Fetch Error:", error);
            }
        }
    }))
})
